import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { deleteAutobookRuleGroup, SaveApiResMessage } from '@/actions';
import { Icons } from '@/assets';
import { Icon } from '@/components/Icon';
import filterStyles from '@/ctComponents/Filter/styles.module.css';
import IconButton from '@/ctComponents/IconButton';
import Modal from '@/ctComponents/Modal';
import optionLabelStyles from './AutobookRuleOptionLabel.module.css';
import { AutobookRuleWarehouseGroupModal } from './AutobookRuleWarehouseGroupModal';

const GROUPS_LIST_MAX_HEIGHT = 100;

export function groupUuidForApi(g) {
  if (!g || typeof g !== 'object') return '';
  const u = g.uuid ?? g.id;
  return u != null ? String(u).trim() : '';
}

export const AutobookRuleWarehouseGroupsSection = ({
  sectionTitle,
  intro,
  subsectionTitle,
  catalogWarehouseGroups = [],
  selectedGroupCatalogIds = [],
  setSelectedGroupCatalogIds,
  catalogCustomers = [],
  catalogWarehouses = [],
  draftRuleUuid,
  instanceId = 'default',
}) => {
  const selectedGroupIds = useMemo(
    () => new Set(selectedGroupCatalogIds.map(String)),
    [selectedGroupCatalogIds]
  );

  const [warehouseGroups, setWarehouseGroups] = useState([]);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groupModalMode, setGroupModalMode] = useState('create');
  const [editingWarehouseGroup, setEditingWarehouseGroup] = useState(null);
  const [deleteGroupModal, setDeleteGroupModal] = useState({
    show: false,
    group: null,
  });
  const [deletingGroupUuid, setDeletingGroupUuid] = useState(null);

  useEffect(() => {
    setWarehouseGroups(
      Array.isArray(catalogWarehouseGroups)
        ? catalogWarehouseGroups.map((g) => ({ ...g }))
        : []
    );
  }, [draftRuleUuid, catalogWarehouseGroups, instanceId]);

  const toggleGroup = useCallback(
    (id) => {
      if (typeof setSelectedGroupCatalogIds !== 'function') return;
      const sid = String(id);
      setSelectedGroupCatalogIds((prev) => {
        const next = new Set((prev ?? []).map(String));
        if (next.has(sid)) next.delete(sid);
        else next.add(sid);
        return [...next];
      });
    },
    [setSelectedGroupCatalogIds]
  );

  const handleGroupCreated = useCallback((group) => {
    setWarehouseGroups((prev) => [...prev, group]);
  }, []);

  const handleGroupUpdated = useCallback((updated) => {
    setWarehouseGroups((prev) =>
      prev.map((x) =>
        x.id === updated.id
          ? {
              ...x,
              name: updated.name,
              uuid: updated.uuid ?? x.uuid,
              groupWarehouseUuids: updated.groupWarehouseUuids ?? [],
              groupCustomerUuids: updated.groupCustomerUuids ?? [],
            }
          : x
      )
    );
  }, []);

  const openCreateGroupModal = useCallback(() => {
    setGroupModalMode('create');
    setEditingWarehouseGroup(null);
    setShowGroupModal(true);
  }, []);

  const openEditGroupModal = useCallback((g) => {
    setGroupModalMode('edit');
    setEditingWarehouseGroup(g);
    setShowGroupModal(true);
  }, []);

  const setGroupModalShow = useCallback((open) => {
    const next = typeof open === 'boolean' ? open : false;
    setShowGroupModal(next);
    if (!next) {
      setGroupModalMode('create');
      setEditingWarehouseGroup(null);
    }
  }, []);

  const handleConfirmDeleteGroup = useCallback(() => {
    const g = deleteGroupModal.group;
    const uuid = groupUuidForApi(g);
    if (!g || !uuid || deletingGroupUuid) return;
    setDeletingGroupUuid(uuid);
    deleteAutobookRuleGroup(uuid)
      .then((res) => {
        SaveApiResMessage({
          title: res?.message || 'Success',
          message: 'Warehouse group deleted successfully',
          mode: 'success',
        });
        setWarehouseGroups((prev) => prev.filter((x) => x.id !== g.id));
        if (typeof setSelectedGroupCatalogIds === 'function') {
          setSelectedGroupCatalogIds((prev) =>
            (prev ?? []).filter((x) => String(x) !== String(g.id))
          );
        }
        setDeleteGroupModal({ show: false, group: null });
      })
      .catch((err) => {
        SaveApiResMessage({
          title: 'Error',
          message: err,
          mode: 'error',
        });
      })
      .finally(() => setDeletingGroupUuid(null));
  }, [deleteGroupModal.group, deletingGroupUuid, setSelectedGroupCatalogIds]);

  return (
    <>
      <section className="box-border flex w-full min-w-0 max-w-full flex-col rounded-lg border border-solid border-[#e8e8e8] bg-white p-4">
        <h2 className="m-0 shrink-0 text-left text-base font-bold leading-snug text-[#131517] font-['Averta',sans-serif]">
          {sectionTitle}
        </h2>
        <p
          className="m-0 mt-2 max-w-[920px] shrink-0 text-left font-normal leading-relaxed text-[#717784] font-['Averta',sans-serif]"
          style={{ fontSize: 14 }}>
          {intro}
        </p>

        <h3 className="m-0 mt-4 shrink-0 text-left text-sm font-bold leading-tight text-[#131517]">
          {subsectionTitle}
        </h3>

        <div
          className="box-border mt-3 w-full min-w-0 max-w-full overflow-x-hidden"
          style={{
            maxHeight:
              warehouseGroups.length > 0 ? GROUPS_LIST_MAX_HEIGHT : undefined,
            overflowY: warehouseGroups.length > 0 ? 'auto' : 'visible',
            boxSizing: 'border-box',
            paddingRight: 4,
          }}>
          <div
            className="grid min-w-0 gap-2"
            style={{
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            }}>
            {warehouseGroups.map((g) => {
              const delUuid = groupUuidForApi(g);
              return (
                <div
                  key={g.id}
                  role="button"
                  tabIndex={0}
                  title="Edit group"
                  onClick={() => openEditGroupModal(g)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openEditGroupModal(g);
                    }
                  }}
                  className="box-border flex min-w-0 max-w-full flex-row items-center justify-between gap-2 rounded-md border border-solid border-[#e8e8e8] bg-white px-2 py-1.5 cursor-pointer transition-colors hover:bg-[#f7f8fa]">
                  <div className="flex min-w-0 min-h-0 flex-1 items-center gap-1.5">
                    <div
                      className="flex shrink-0 items-center"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className={`${filterStyles.checkBox} shrink-0`}
                        checked={selectedGroupIds.has(g.id)}
                        onChange={() => toggleGroup(g.id)}
                      />
                    </div>
                    <span
                      className={`${optionLabelStyles.labelText} min-w-0 max-w-full flex-1 truncate text-left text-sm`}
                      title={g.name}>
                      {g.name}
                    </span>
                  </div>
                  <button
                    type="button"
                    title="Delete group"
                    disabled={!delUuid || deletingGroupUuid === delUuid}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteGroupModal({ show: true, group: g });
                    }}
                    className="shrink-0 rounded border border-solid border-error-light bg-transparent p-1.5 shadow-none hover:bg-gray-100 cursor-pointer disabled:cursor-not-allowed disabled:opacity-45">
                    <Icon
                      color="#D02533"
                      src={Icons.trashIcon}
                      width={14}
                      height={14}
                    />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
        <div className="mt-3 flex w-full min-w-0 shrink-0 flex-row flex-wrap items-center justify-between gap-2">
          <p className="m-0 min-w-0 flex-1 text-left text-xs leading-snug text-inactive-text">
            {selectedGroupIds.size} group(s) selected
          </p>
          <IconButton
            text="Create Group"
            icon="Add"
            size="mini"
            onClick={openCreateGroupModal}
            color="#131517"
            border="1px solid #e8e8e8"
            style={{
              height: 32,
              borderRadius: 4,
              minWidth: 0,
              backgroundColor: '#fff',
              fontWeight: 600,
              fontSize: 12,
            }}
          />
        </div>
      </section>

      <AutobookRuleWarehouseGroupModal
        show={showGroupModal}
        setShow={setGroupModalShow}
        mode={groupModalMode}
        editingGroup={editingWarehouseGroup}
        onCreated={handleGroupCreated}
        onUpdated={handleGroupUpdated}
        customers={catalogCustomers}
        warehouses={catalogWarehouses}
      />

      <Modal
        show={deleteGroupModal.show}
        setShow={() => setDeleteGroupModal({ show: false, group: null })}
        submit={handleConfirmDeleteGroup}
        submitText="Yes, Delete"
        loadingbutton={
          deletingGroupUuid === groupUuidForApi(deleteGroupModal.group)
        }
        icon="deleteIcon"
        title="Delete warehouse group">
        <div className="flex w-full flex-col items-center justify-center gap-4 py-2">
          <span className="font-bold text-primary-text">
            Are you sure you want to delete this warehouse group?
          </span>
          <span className="max-w-480 mx-auto px-10 text-center text-secondary-text">
            &quot;{deleteGroupModal.group?.name ?? ''}&quot; will be removed
            permanently.
          </span>
        </div>
      </Modal>
    </>
  );
};
